import React, { useState, useEffect, useRef } from "react"
import styled, { css } from "styled-components"
import ResizeObserver from "resize-observer-polyfill"
import { useStaticQuery, graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-react-intl"
import { useLocation } from "@reach/router"
import { useSpring, animated } from "react-spring"
import useMeasure from "react-use-measure"
import { useClickAway } from "react-use"

import Button from "./Button"
import Snippet from "./Snippet"
import StyledLink from "./Link"

import { media } from "../styles"
import { isBrowser, getParentPath } from "../helpers"
import NavToggleSVG from "../images/icons/nav-toggle.inline.svg"
import NavCloseSVG from "../images/icons/nav-close.inline.svg"
import SubMenuToggleSVG from "../images/icons/submenu-toggle.inline.svg"
import BackSVG from "../images/icons/back--small.inline.svg"
import ExternalSVG from "../images/icons/external.inline.svg"
import HeaderSearch from "./HeaderSearch"
import NavigationGrid, { SubNavStyles } from "./NavigationGrid"
import NavigationIconGrid from "./NavigationIconGrid"

const NavContainer = styled.div`
  position: relative;
  z-index: 99999;

  @media ${media.lg} {
    display: none;
  }

  @media print {
    display: none;
  }
`

const Toggle = styled.button`
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.spaceblue};
  cursor: pointer;
  display: flex;
  height: 30px;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 30px;
`

const Flyout = styled(animated.nav)`
  background: ${({ theme }) => theme.greyLight};
  bottom: 0;
  box-shadow: -1px 0 0 0 ${({ theme }) => `rgba(${theme.sageRGB}, 0.7)`};
  max-width: 95%;
  overflow: auto;
  padding: 80px 0 40px;
  position: fixed;
  right: -1px;
  top: 0;
  width: 470px;

  .button {
    margin-top: 20px;
  }

  @media ${media.md} {
    padding: 150px 0 40px 0;
  }
`

const Close = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.spaceblue};
  cursor: pointer;
  margin: 0;
  padding: 0;
  position: absolute;
  right: 10%;
  top: 20px;
`

const Menu = styled.ul`
  font-size: 21px;
  font-weight: 700;
  letter-spacing: 0.6px;
  list-style: none;
  margin: 0 0 30px;
  padding: 0;
  text-transform: uppercase;
  width: 95%; // menu is 5% out of viewport

  a {
    align-items: center;
    color: inherit;
    display: flex;
    text-decoration: none;

    svg {
      flex: none;
    }

    svg:first-child {
      margin-right: 11px;
    }

    svg:last-child {
      margin-left: 11px;
    }

    &:hover {
      color: ${({ theme }) => theme.sun};
    }
  }

  & & {
    ${SubNavStyles}
  }
`

const MenuItem = styled.li`
  border-bottom: 1px solid ${({ theme }) => theme.greyWarm};
  margin: 0;
  padding: 16px 20px;

  &:last-child {
    border: none;
  }

  ${Menu} ${Menu} & {
    border: none;
    line-height: 1.25;
    margin: 0;
    padding: 5px 0;
  }
`

const Link = styled(StyledLink)`
  ul ul &.current {
    color: ${({ theme }) => theme.sun};
    font-weight: 700;
  }
`

const SubmenuToggle = styled.div`
  cursor: pointer;
  display: flex;
  width: 100%;

  &:hover {
    color: ${({ theme }) => theme.sun};
  }

  ${(props) =>
    props.$isOpen &&
    css`
      ${SubmenuToggleIcon} {
        transform: rotate(180deg);
      }
    `}
`

const SubmenuToggleIcon = styled.span`
  align-items: center;
  display: flex;
  margin: 0 0 0 auto;
  transition: transform ${({ theme }) => theme.transitionTime};
`

const Search = styled(HeaderSearch)`
  .search-label {
    margin-left: -2px;
  }
`

const ButtonRow = styled.div`
  padding: 0 20px;
`

const SubNav = ({ isOpen, mode, children }) => {
  const [menuHeight, setMenuHeight] = useState("0px")
  const [ref, { height }] = useMeasure({ polyfill: ResizeObserver })

  const expand = useSpring({
    config: {
      friction: 30,
      tension: 300,
    },
    height: isOpen ? `${menuHeight + 40}px` : "0px",
    opacity: isOpen ? 1 : 0,
    overflow: "hidden",
    paddingBottom: isOpen ? "19px" : "0px",
    paddingTop: isOpen ? "19px" : "0px",
  })

  useEffect(() => {
    //Sets initial height
    setMenuHeight(height)
  }, [height, isOpen])

  return (
    <animated.div style={expand}>
      <div ref={ref}>
        {mode === "grid" ? <>{children}</> : <Menu>{children}</Menu>}
      </div>
    </animated.div>
  )
}

const HeaderNavigationMobile = (props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [subOpen, setSubOpen] = useState()
  const intl = useIntl()
  const { pathname } = useLocation()
  let body, bodyClassList
  const ref = useRef()

  const flyoutSpring = useSpring({
    config: {
      friction: 30,
      tension: 300,
    },
    transform: isOpen ? `translate3d(5%, 0, 0)` : `translate3d(100%, 0, 0)`,
  })

  if (isBrowser()) {
    body = document.querySelector("body")
    bodyClassList = body.classList
  }

  useEffect(() => {
    bodyClassList.remove("scroll-locked")
    setSubOpen(getParentPath(pathname))
  }, [bodyClassList, pathname])

  const toggle = () => {
    setIsOpen(!isOpen)
    bodyClassList.toggle("scroll-locked")
  }

  const toggleSubMenu = (path) => {
    setSubOpen(subOpen === path ? undefined : path)
  }

  useClickAway(ref, () => {
    isOpen && toggle()
  })

  const data = useStaticQuery(graphql`
    query HeaderNavigationMobileQuery {
      allNavigationJson {
        nodes {
          locale
          items {
            label
            slug
            mobileOnly
            url
            mode
            items {
              label
              slug
            }
          }
        }
      }
      allProductCollections: allContentfulProductCollection(
        filter: { visible: { eq: true }, isOld: { ne: true } }
        sort: { sortOrder: ASC }
      ) {
        nodes {
          id
          node_locale
          icon: iconSmall {
            ...ContentfulImageData
          }
          name
          items: products {
            ... on ContentfulProduct {
              name
              slug
            }
          }
        }
      }
      allIndustries: allContentfulIndustry(
        filter: {
          hasIndustryPage: { eq: true }
          hideIndustryPageFromNavigation: { ne: true }
        }
        sort: { sortOrder: ASC }
      ) {
        nodes {
          id
          name
          node_locale
          slug
          icon: iconSmall {
            ...ContentfulImageData
          }
        }
      }
    }
  `)

  const navItems = data.allNavigationJson.nodes.find(
    (node) => node.locale === intl.locale
  )?.items

  const productCollections = data.allProductCollections.nodes.filter(
    (node) => node.node_locale === intl.locale
  )

  const industries = data.allIndustries.nodes.filter(
    (node) => node.node_locale === intl.locale
  )

  const renderMenuItem = (item, subOpen, prefix) => {
    if (item.items?.length > 0) {
      return (
        <SubmenuToggle
          onClick={() => toggleSubMenu(item.slug)}
          $isOpen={subOpen === item.slug}
        >
          {item.label}
          <SubmenuToggleIcon aria-hidden="true">
            <SubMenuToggleSVG />
          </SubmenuToggleIcon>
        </SubmenuToggle>
      )
    } else if (item.slug) {
      const slug = prefix ? `${prefix}/${item.slug}/` : item.slug
      return (
        <Link to={slug} activeClassName="current" intl={item.intl === false}>
          {item.label}
        </Link>
      )
    } else if (item.url) {
      return (
        <a href={item.url} target="_blank" rel="noopener noreferrer">
          {item.icon === "back" && <BackSVG />} <span>{item.label}</span>{" "}
          {item.icon === "ext" && <ExternalSVG />}
        </a>
      )
    } else {
      return item.label
    }
  }

  return (
    <NavContainer {...props}>
      <Toggle
        onClick={() => toggle()}
        id="mobile-menu"
        aria-label={intl.formatMessage({
          id: "accessibility.regions.mainNavigation",
        })}
      >
        <NavToggleSVG aria-hidden="true" />
      </Toggle>
      <Flyout
        aria-labelledby={`mobile-menu`}
        role="navigation"
        style={flyoutSpring}
        ref={ref}
      >
        <Close
          onClick={() => toggle()}
          aria-label={intl.formatMessage({
            id: "close",
          })}
        >
          <NavCloseSVG aria-hidden="true" />
        </Close>

        <Menu>
          {navItems.map((navItem) => (
            <MenuItem key={navItem.label}>
              {renderMenuItem(navItem, subOpen)}
              {navItem.items && (
                <SubNav isOpen={subOpen === navItem.slug} mode={navItem.mode}>
                  {navItem.items.map((subItem) => {
                    switch (subItem.label) {
                      case "RENDER_SERVICES":
                        return (
                          <NavigationGrid
                            basePath="/services/"
                            iconColor="sun"
                            items={productCollections}
                            key={subItem.label}
                          />
                        )
                      case "RENDER_INDUSTRIES":
                        return (
                          <NavigationIconGrid
                            basePath="/industries/"
                            iconColor="sun"
                            items={industries}
                            key={subItem.label}
                            mode="topnav"
                          />
                        )
                      default:
                        return (
                          <MenuItem key={subItem.label}>
                            {renderMenuItem(subItem, subOpen)}
                          </MenuItem>
                        )
                    }
                  })}
                </SubNav>
              )}
            </MenuItem>
          ))}
          <MenuItem>
            <Search
              mode="link"
              label={intl.formatMessage({
                id: "header.shortlinks.search",
              })}
            />
          </MenuItem>
        </Menu>
        <ButtonRow>
          <Button
            arrow
            as={Link}
            highlight
            intl={false}
            to={Snippet({ id: "navigationCTA", field: "linkUrl" })}
          >
            <Snippet id="navigationCTA" field="linkText" />
          </Button>
        </ButtonRow>
      </Flyout>
    </NavContainer>
  )
}

export default HeaderNavigationMobile
